// @flow

import { get } from 'lodash';
import { useEffect, useReducer } from 'react';

import { mapErrorMessage } from '@zapier/payment/src/utils';
import {
  type PaymentSource,
  type PaymentSourceBackend,
  type PaymentSourceMethod,
  paymentSourceMethods,
} from '@zapier/payment';

import usePaymentSources from './usePaymentSources';
import useAttemptTracking, {
  attemptEvents,
  attemptNodes,
} from './useAttemptTracking';

type Input = {
  accountId: number,
  onSaveSuccess: () => void,
  onTrack: (
    string,
    {
      attemptId: string,
      attemptPath: string,
    }
  ) => void,
  startEdit: boolean,
};

type Output = {
  backend: ?PaymentSourceBackend,
  currentSource: ?PaymentSource,
  error: ?string,
  forceCardUpdate: boolean,
  isEditing: boolean,
  isLoading: boolean,
  isSaving: boolean,
  method: ?PaymentSourceMethod,
  onCancelEdit: () => void,
  onEdit: PaymentSourceMethod => void,
  onError: (error: Error, method: PaymentSourceMethod) => void,
  onHideSuccessMessage: () => void,
  onSave: ({}, PaymentSourceMethod) => void,
  onSelectMethod: (PaymentSourceMethod, boolean) => void,
  showSuccessMessage: boolean,
  supportedMethods: Array<PaymentSourceMethod>,
};

type State = {
  error: ?string,
  isEditing: boolean,
  method: ?PaymentSourceMethod,
  showSuccessMessage: boolean,
};

const actions = {
  CANCEL_EDIT: 'CANCEL_EDIT',
  EDIT_DETAILS: 'EDIT_DETAILS',
  HIDE_SUCCESS_MESSAGE: 'HIDE_SUCCESS_MESSAGE',
  SAVE_DETAILS: 'SAVE_DETAILS',
  SAVE_DETAILS_DONE: 'SAVE_DETAILS_DONE',
  SAVE_DETAILS_FAIL: 'SAVE_DETAILS_FAIL',
  SELECT_METHOD: 'SELECT_METHOD',
  SET_METHOD: 'SET_METHOD',
};

const initialState = {
  error: null,
  isEditing: false,
  method: null,
  showSuccessMessage: false,
};

const reducer = (state: State, action) => {
  switch (action.type) {
    case actions.CANCEL_EDIT:
      return {
        ...initialState,
        method: action.method,
      };

    case actions.EDIT_DETAILS:
      return {
        ...state,
        isEditing: true,
        showSuccessMessage: false,
      };

    case actions.HIDE_SUCCESS_MESSAGE:
      return {
        ...state,
        showSuccessMessage: false,
      };

    case actions.SAVE_DETAILS:
      return {
        ...state,
        error: null,
      };

    case actions.SAVE_DETAILS_DONE:
      return {
        ...state,
        error: null,
        isEditing: false,
        showSuccessMessage: true,
      };

    case actions.SAVE_DETAILS_FAIL:
      return {
        ...state,
        error: action.message,
      };

    case actions.SELECT_METHOD:
      return {
        error: null,
        isEditing: true,
        method: action.method,
        showSuccessMessage: false,
      };

    case actions.SET_METHOD:
      return {
        ...state,
        method: action.method,
      };

    default:
      throw new Error();
  }
};

const getPaymentMethod = (
  savedSource: ?PaymentSource,
  method: ?PaymentSourceMethod
) => {
  if (method) return method;

  return get(savedSource, 'type');
};

const usePaymentSourceState = ({
  accountId,
  onSaveSuccess,
  onTrack,
  startEdit,
}: Input): Output => {
  const [state, dispatch] = useReducer<State, any>(reducer, {
    ...initialState,
    isEditing: startEdit,
  });
  const { resetAttempt, trackAttempt } = useAttemptTracking({ onTrack });

  const onError = (error: Error, method: PaymentSourceMethod) => {
    // Only dispatch error if the current payment method hasn't changed since the error was thrown
    if (state.method === method) {
      const message = mapErrorMessage(error);

      trackAttempt({ event: attemptEvents.error, path: attemptNodes.error });

      dispatch({
        message,
        type: actions.SAVE_DETAILS_FAIL,
      });
    }
  };

  const onSuccess = ({ saveBankPaymentSource }) => {
    dispatch({
      type: actions.SAVE_DETAILS_DONE,
    });
    // fire off any side-effects in app
    onSaveSuccess();
    trackAttempt({
      event: attemptEvents.save,
      path: attemptNodes.save,
      country:
        get(saveBankPaymentSource, ['details', 'address_country']) ||
        get(saveBankPaymentSource, ['addressCountry']),
      type:
        get(saveBankPaymentSource, ['type']) ||
        get(saveBankPaymentSource, ['method']),
    });
    resetAttempt();
  };

  const paymentSources = usePaymentSources({ accountId, onError, onSuccess });

  // Selects the primary payment source method (if exists) when it
  // first loads
  useEffect(() => {
    if (!state.method && paymentSources.primarySource) {
      dispatch({
        method: getPaymentMethod(paymentSources.primarySource),
        type: actions.SET_METHOD,
      });
    }
    // eslint-disable-next-line
  }, [paymentSources.primarySource]);

  const onSelectMethod = (
    method: PaymentSourceMethod,
    hasSwitched: boolean
  ) => {
    dispatch({
      method: getPaymentMethod(paymentSources.primarySource, method),
      type: actions.SELECT_METHOD,
    });
    trackAttempt({
      event: attemptEvents.switch,
      path: attemptNodes[`${hasSwitched ? 'switch' : 'select'}-${method}`],
    });
  };

  const onEdit = (method: PaymentSourceMethod) => {
    dispatch({
      method: getPaymentMethod(paymentSources.primarySource),
      type: actions.EDIT_DETAILS,
    });
    trackAttempt({
      data: {
        isNew: false,
        method,
      },
      event: attemptEvents.start,
      path: attemptNodes.edit,
    });
  };

  const onSave = details => {
    // $FlowFixMe - state.method cannot be null in this situation
    paymentSources.onSave(details, state.method);
  };

  const onCancelEdit = () => {
    trackAttempt({
      event: attemptEvents.cancel,
      path: attemptNodes.cancel,
    });
    resetAttempt();
    dispatch({
      method: getPaymentMethod(paymentSources.primarySource),
      type: actions.CANCEL_EDIT,
    });
  };

  const onHideSuccessMessage = () => {
    dispatch({ type: actions.HIDE_SUCCESS_MESSAGE });
  };

  const { primarySource } = paymentSources;

  // used to force users to update their CC details if they do not
  // have a country saved
  const forceCardUpdate = Boolean(
    primarySource &&
      primarySource.type === paymentSourceMethods.card &&
      !primarySource.details.addressCountry
  );

  return {
    backend: paymentSources.paymentBackend,
    currentSource: paymentSources.primarySource,
    error: state.error,
    forceCardUpdate,
    isEditing: state.isEditing,
    isLoading: paymentSources.isLoading,
    isSaving: paymentSources.isSaving,
    method: state.method,
    onCancelEdit,
    onEdit,
    onError,
    onHideSuccessMessage,
    onSave,
    onSelectMethod,
    showSuccessMessage: state.showSuccessMessage,
    supportedMethods: paymentSources.supportedPaymentMethods,
  };
};

export default usePaymentSourceState;
